import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { useContext, useEffect, useState } from 'react';
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import ApplicationContext from '../../../ApplicationContext';
import { getTbNameByJobName, JobNames } from '../../../framework.core/JobNames';
import useDirtyCheck from '../../../hooks/useDirtyCheck2';

import HawbMilestoneView from './implementation/HawbMilestone';

import { useTypedFastStorage } from '../../../hooks/useFastStorage';

import { HblStatusViewModel } from '../../../fms.core/HblStatusViewModel';
import useGlobalOkDialog from '../../../hooks/useGlobalOkDialog';
import SecondaryButton from '../../../ui/SecondaryButton';
import { ApiResult, InsertResultsWithData } from '../../../framework.core/data/ApiResult';
import { SQLInfo } from '../../../framework.core/data/SQLInfo';
import { historyUpdate } from '../../../fms.data/modules/FmsModFunc';
import { getSQLInfoList2 } from '../../../fms.core/data/TrackingModule';
import { TrackableEntity } from '../../../fms.core/data/ChangeTrackable';
import { stringsEqualIgnoreCase } from '../../../util';

function HawbBlStatusImpl({
  hawbNo,
  departure,
  arrival,
  etd,
  eta,
  hawbId,
  storageKey,
  onClose,
  isView,
}: {
  hawbNo: string,
  departure: string,
  arrival: string,
  etd: string|undefined,
  eta: string|undefined,
  hawbId: number,
  storageKey: string,
  onClose: VoidFunction,
  isView: boolean,
}) {
  const appContext = useContext(ApplicationContext);

  const [showGlobalOkDialog] = useGlobalOkDialog();

  const [initialized, setInitialized] = useState(false);

  const [blStatus, setBlStatus, removeBlStatus, blStatusRef] = useTypedFastStorage<HblStatusViewModel>(storageKey, null);
  const {setCleanState, /*isDirtyComparedTo,*/ dirtyCleanup} = useDirtyCheck(storageKey, ['updated', 'updatedBy']);


  useEffect(() => {
      initialize();
      return () => {
        removeBlStatus();
        dirtyCleanup();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  async function loadBlStatus() {
    const apiResult:ApiResult<HblStatusViewModel> = await appContext.api.getHblStatusViewModel(JobNames.AEHbl, hawbId);

    if (!apiResult || !apiResult.success) {
      await showGlobalOkDialog('Cannot load data');
      onClose();
      return;
    }

    const loadedModelObj = new HblStatusViewModel(apiResult.result);
    setCleanState(loadedModelObj);

    setBlStatus(new HblStatusViewModel(_.cloneDeep(loadedModelObj)));

    if(blStatusRef.current !== null) {
      blStatusRef.current.trackable = true;
    }

    if (!apiResult.result.cargoOnBoard) {
      setBlStatus({cargoOnBoard: 'Cargo on Board'})
    }

    setInitialized(true);
  }

  async function initialize() {
    await loadBlStatus();
  }

  async function save() {
    if (!blStatus || !blStatusRef.current) {
      throw new Error('Invalid operation');
    }

    if(!blStatusRef.current.isDirty) {
      //await showGlobalOkDialog(<p>Nothing to save!!!</p>);
      onClose();
      return;
    }

    const si:SQLInfo[] = [];

    let hiSqlInfo = historyUpdate(getTbNameByJobName(JobNames.AEHbl), hawbId, appContext.user?.id??'', 'Update B/L Info');
    if (hiSqlInfo) {
      si.push(hiSqlInfo);
    }
    
    const sqlInfoList = getSQLInfoList2(blStatusRef.current as TrackableEntity, HblStatusViewModel.getFieldMappings(JobNames.AEHbl));
    sqlInfoList.push(...si);

    sqlInfoList.filter(x => stringsEqualIgnoreCase(x.tableName, "t_aotherinfo")).forEach(x => {
      SQLInfo.setWhereFieldValueByFieldName(x, 'f_tbid', hawbId);
    });

    const apiResult:ApiResult<InsertResultsWithData> = await appContext.api.saveHblStatusViewModel(sqlInfoList);

    if (apiResult && apiResult.success) {
      await showGlobalOkDialog(<p>The data has been saved</p>);
      onClose();
      
    } else {
      await showGlobalOkDialog(<div>
        <p>Cannot save</p>
        <p>{apiResult?.message}</p>
      </div>);
    }
  }
  
  /*
  async function UpdateLogHawbMilestone() {
    if (!blStatus) return;

    let cmile:BlEventLog[] = [];

    if (checkPropDirty(blStatus, 'anPrtDate')) {
      cmile.push(new BlEventLog(BlEventName.Fms7501, blStatus.anPrtDate))
    }
    if (checkPropDirty(blStatus, 'delSchedDate')) {
      cmile.push(new BlEventLog(BlEventName.Fms3461, blStatus.delSchedDate))
    }
    if (checkPropDirty(blStatus, 'delActDate')) {
      cmile.push(new BlEventLog(BlEventName.FmsDelSched, blStatus.delActDate))
    }
    if (checkPropDirty(blStatus, 'emptyRetDate')) {
      cmile.push(new BlEventLog(BlEventName.FmsDelAct, blStatus.emptyRetDate))
    }

    if (cmile.length > 0) {
      const tempData = cmile.sort((a, b) => new Date(a.eventAt ?? '').getTime() - new Date(b.eventAt ?? '').getTime());
      const lastEvents: BlEventLog = tempData[tempData.length - 1];
      lastEvents.suntekID = getFmsGlobal().AcmeID ?? '';
      lastEvents.jobName = JobNames.AEHbl;
      lastEvents.jobId = hawbId;
      lastEvents.blNo = blStatus.hblNo;
      //lastEvents.eventAtUtc
      lastEvents.u1Id = appContext.user?.id ?? '';
      const apiResult = await appContext.api.trackingLogHblMilestone(lastEvents);;
      if (!apiResult.success) {
        await showGlobalOkDialog(<p>{apiResult.message}</p>);
      }
    }
  }
*/

  return (
    <div>
      {!initialized &&
        <div style={{position: 'relative', top: '-25px'}}>
          <SkeletonTheme color="#4287f5" highlightColor="#b9d2fa">
            <Skeleton count={1} height={2} />
          </SkeletonTheme>
        </div>
      }
      {!!blStatus &&
        <div className='report-popup'>
          <HawbBlStatusInitializedImpl setBlStatus={setBlStatus} blStatus={blStatus} isView={isView} />

          <div className='report-footer'>
            <SecondaryButton
              size="regular"
              style={{width: '90px'}}
              onClick={save}>
              Update
            </SecondaryButton>
            <SecondaryButton
              size="regular"
              style={{width: '90px'}}
              onClick={onClose}>
              Cancel
            </SecondaryButton>
          </div>
        </div>
      }
    </div>
  );
}

function HawbBlStatusInitializedImpl({
  isView,
  blStatus,
  setBlStatus
}: {
  isView: boolean,
  blStatus: HblStatusViewModel,
  setBlStatus: (x: Partial<HblStatusViewModel>) => void,
}) {
  return (
    <HawbMilestoneView blStatus={blStatus} onChange={setBlStatus} isView={isView} />
  );
}

const HawbBlStatusPopup = ({
  hawbNo,
  departure,
  arrival,
  etd,
  eta,
  hawbId,
  storageKey,
  onClose,
  isView,
}:{
  hawbNo: string,
  departure: string,
  arrival: string,
  etd: string|undefined,
  eta: string|undefined,
  hawbId: number,
  storageKey: string,
  onClose: VoidFunction,
  isView: boolean,
}) => {
  return (
    <HawbBlStatusImpl hawbNo={hawbNo} departure={departure} arrival={arrival} etd={etd} eta={eta} hawbId={hawbId} key={storageKey} storageKey={storageKey} onClose={onClose} isView={isView} />
  );
};

HawbBlStatusPopup.defaultProps = {
};

HawbBlStatusPopup.propTypes = {
  hawbId: PropTypes.number.isRequired,
  storageKey: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  isView: PropTypes.bool.isRequired,
};

export default React.memo(HawbBlStatusPopup);
